import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

// Cart actions
const ADD_TO_CART = 'ADD_TO_CART';
const REMOVE_FROM_CART = 'REMOVE_FROM_CART';
const CLEAR_CART = 'CLEAR_CART';
const SET_EMAIL = 'SET_EMAIL';
const ADD_TO_CATALOG_CART = 'ADD_TO_CATALOG_CART';
const REMOVE_FROM_CATALOG_CART = 'REMOVE_FROM_CATALOG_CART';
const CLEAR_CATALOG_CART = 'CLEAR_CATALOG_CART';
const SET_NAME = 'SET_NAME';
const SET_NOTES = 'SET_NOTES';
const UPDATE_CATALOG_ITEM_QUANTITY = 'UPDATE_CATALOG_ITEM_QUANTITY';
const REMOVE_CATALOG_ITEM_QUANTITY = 'REMOVE_CATALOG_ITEM_QUANTITY';
const SET_USB_DRIVE_OPTION = 'SET_USB_DRIVE_OPTION';

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case ADD_TO_CART:
      const existingItem = state.items.find(item => item.key === action.payload.key);
      if (existingItem) {
        return state; // Don't add duplicates
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, price: state.photoPrice }]
      };
    
    case REMOVE_FROM_CART:
      return {
        ...state,
        items: state.items.filter(item => item.key !== action.payload)
      };
    
    case CLEAR_CART:
      return {
        ...state,
        items: [],
        usbDriveOption: false
      };
    
    case SET_EMAIL:
      return {
        ...state,
        email: action.payload
      };
    
    case ADD_TO_CATALOG_CART:
      console.log('ADD_TO_CATALOG_CART action:', action);
      const existingCatalogItem = state.catalogItems.find(item => item.id === action.payload.id);
      
      if (existingCatalogItem) {
        // If item already exists, increment quantity
        const updatedItems = state.catalogItems.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: (item.quantity || 1) + 1 }
            : item
        );
        return {
          ...state,
          catalogItems: updatedItems
        };
      } else {
        // Add new item with quantity 1
        const newState = {
          ...state,
          catalogItems: [...(state.catalogItems || []), { ...action.payload, quantity: 1 }]
        };
        console.log('New state after ADD_TO_CATALOG_CART:', newState);
        return newState;
      }
    
    case UPDATE_CATALOG_ITEM_QUANTITY:
      return {
        ...state,
        catalogItems: state.catalogItems.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
    
    case REMOVE_CATALOG_ITEM_QUANTITY:
      return {
        ...state,
        catalogItems: state.catalogItems.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: Math.max(0, (item.quantity || 1) - action.payload.quantity) }
            : item
        ).filter(item => (item.quantity || 1) > 0)
      };
    
    case REMOVE_FROM_CATALOG_CART:
      return {
        ...state,
        catalogItems: state.catalogItems.filter(item => item.id !== action.payload)
      };
    
    case CLEAR_CATALOG_CART:
      return {
        ...state,
        catalogItems: [],
        usbDriveOption: false
      };
    
    case SET_NAME:
      return {
        ...state,
        name: action.payload
      };
    
    case SET_NOTES:
      return {
        ...state,
        notes: action.payload
      };

    case 'SET_PHOTO_PRICE':
      return {
        ...state,
        photoPrice: action.payload
      };
    
    case SET_USB_DRIVE_OPTION:
      return {
        ...state,
        usbDriveOption: action.payload
      };
    
    default:
      return state;
  }
};

// Initial state
const initialState = {
  items: [],
  catalogItems: [],
  email: '',
  name: '',
  notes: '',
  photoPrice: 10, // Default price until we fetch from API
  usbDriveOption: false
};

// Load from localStorage with proper initialization
const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem('datPhotoCart');
    if (savedCart) {
      const parsed = JSON.parse(savedCart);
      // Handle migration from old format to new format with quantity
      const migratedCatalogItems = (parsed.catalogItems || []).map(item => ({
        ...item,
        quantity: item.quantity || 1 // Ensure quantity exists for backward compatibility
      }));
      return {
        ...initialState,
        ...parsed,
        catalogItems: Array.isArray(migratedCatalogItems) ? migratedCatalogItems : []
      };
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  return initialState;
};

// Save to localStorage
const saveCartToStorage = (state) => {
  try {
    localStorage.setItem('datPhotoCart', JSON.stringify(state));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

// Cart provider
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState, loadCartFromStorage);

  // Fetch photo pricing on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(
          "https://dat-photo-admin.t9e2i3kal.workers.dev/pricing?ids=photos"
        );
        const data = await response.json();
        if (data.success) {
          dispatch({ 
            type: 'SET_PHOTO_PRICE', 
            payload: data.data.photos 
          });
        }
      } catch (error) {
        console.error('Failed to fetch photo pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    saveCartToStorage(state);
  }, [state]);

  // Actions
  const addToCart = (item) => {
    dispatch({ type: ADD_TO_CART, payload: item });
  };

  const removeFromCart = (key) => {
    dispatch({ type: REMOVE_FROM_CART, payload: key });
  };

  const clearCart = () => {
    dispatch({ type: CLEAR_CART });
  };

  const setEmail = (email) => {
    dispatch({ type: SET_EMAIL, payload: email });
  };

  const getTotalPrice = () => {
    return Number(state.items.length * state.photoPrice);
  };

  const addToCatalogCart = (item) => {
    console.log('Dispatching ADD_TO_CATALOG_CART with:', item);
    dispatch({ type: ADD_TO_CATALOG_CART, payload: item });
  };

  const removeFromCatalogCart = (id) => {
    dispatch({ type: REMOVE_FROM_CATALOG_CART, payload: id });
  };

  const updateCatalogItemQuantity = (id, quantity) => {
    dispatch({ type: UPDATE_CATALOG_ITEM_QUANTITY, payload: { id, quantity } });
  };

  const removeCatalogItemQuantity = (id, quantity = 1) => {
    dispatch({ type: REMOVE_CATALOG_ITEM_QUANTITY, payload: { id, quantity } });
  };

  const clearCatalogCart = () => {
    dispatch({ type: CLEAR_CATALOG_CART });
  };

  const setName = (name) => {
    dispatch({ type: SET_NAME, payload: name });
  };

  const setNotes = (notes) => {
    dispatch({ type: SET_NOTES, payload: notes });
  };

  const setUsbDriveOption = (selected) => {
    dispatch({ type: SET_USB_DRIVE_OPTION, payload: selected });
  };

  const getCatalogTotalPrice = () => {
    const pick5Items = state.catalogItems.filter(item =>
      item.title.includes("Pick 5")
    );
    const regularItems = state.catalogItems.filter(item =>
      !item.title.includes("Pick 5")
    );
    
    // Calculate totals based on quantity
    const pick5Total = pick5Items.reduce((total, item) => {
      const quantity = item.quantity || 1;
      return total + (Number(item.price) * quantity);
    }, 0);
    
    const regularTotal = regularItems.reduce((total, item) => {
      const quantity = item.quantity || 1;
      return total + (Number(item.price) * quantity);
    }, 0);
    
    // Special handling for Pick 5 promotion
    const pick5Count = pick5Items.reduce((count, item) => count + (item.quantity || 1), 0);
    if (pick5Count === 5) {
      return Number(250 + regularTotal);
    }
    
    return Number(pick5Total + regularTotal);
  };

  const getCatalogItemQuantity = (id) => {
    const item = state.catalogItems.find(item => item.id === id);
    return item ? (item.quantity || 1) : 0;
  };

  const value = {
    items: state.items,
    catalogItems: state.catalogItems,
    email: state.email,
    name: state.name,
    addToCart,
    removeFromCart,
    clearCart,
    setEmail,
    getTotalPrice,
    addToCatalogCart,
    removeFromCatalogCart,
    clearCatalogCart,
    setName,
    setNotes,
    getCatalogTotalPrice,
    getCatalogItemQuantity,
    updateCatalogItemQuantity,
    removeCatalogItemQuantity,
    notes: state.notes,
    photoPrice: state.photoPrice,
    usbDriveOption: state.usbDriveOption,
    setUsbDriveOption
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use cart
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
